(() => {
  'use strict';
  const timeMinutes = value => {
    if (typeof value !== 'string' || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)) return null;
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  };
  const getParisClock = (now = new Date()) => {
    const values = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {timeZone:'Europe/Paris',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}).formatToParts(now).map(part => [part.type, part.value]));
    return {iso:`${values.year}-${values.month}-${values.day}`,minutes:Number(values.hour)*60+Number(values.minute),seconds:Number(values.second)};
  };
  const getNextPrayer = (schedule, clock) => {
    const next = ['fajr','maghrib'].find(key => timeMinutes(schedule[key]) > clock.minutes);
    if (!next) return null;
    const remaining = Math.ceil((timeMinutes(schedule[next])*60-clock.minutes*60-clock.seconds)/60);
    return {prayer:next === 'fajr' ? 'Fajr' : 'Maghrib',hours:Math.floor(remaining/60),minutes:String(remaining%60).padStart(2,'0')};
  };
  const validSchedule = schedule => {
    const values = ['imsak','fajr','maghrib'].map(key => timeMinutes(schedule?.[key]));
    return values.every(value => value !== null) && values[1]-values[0] === 10 && values[2] > values[1];
  };
  const getDailyUrl = (iso, coordinates) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || new Date(`${iso}T12:00:00Z`).toISOString().slice(0,10) !== iso) throw new Error('Invalid date');
    if (!Array.isArray(coordinates) || coordinates.length !== 2 || !coordinates.every(Number.isFinite) || Math.abs(coordinates[0]) > 90 || Math.abs(coordinates[1]) > 180) throw new Error('Invalid coordinates');
    const [year,month,day] = iso.split('-');
    const url = new URL(`https://api.aladhan.com/v1/timings/${day}-${month}-${year}`);
    url.search = new URLSearchParams({latitude:coordinates[0],longitude:coordinates[1],method:3,timezonestring:'Europe/Paris',latitudeAdjustmentMethod:3});
    return url.href;
  };
  const parseTimings = (body, iso) => {
    const data = body?.data;
    if (body?.code !== 200 || data?.date?.gregorian?.date !== iso.split('-').reverse().join('-') || data?.meta?.timezone !== 'Europe/Paris' || data?.meta?.method?.id !== 3) throw new Error('Unexpected prayer data');
    const schedule = Object.fromEntries(['Imsak','Fajr','Maghrib'].map(key => [key.toLowerCase(),String(data.timings?.[key] || '').split(' ')[0]]));
    if (!validSchedule(schedule)) throw new Error('Invalid prayer times');
    return schedule;
  };
  const createClient = fetcher => {
    const cache = new Map();
    return (iso, coordinates) => {
      const url = getDailyUrl(iso, coordinates);
      if (!cache.has(url)) {
        if (cache.size >= 36) cache.delete(cache.keys().next().value);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        const request = Promise.resolve().then(() => fetcher(url,{signal:controller.signal,credentials:'omit',referrerPolicy:'no-referrer'})).then(response => {
          if (!response.ok) throw new Error('Prayer source unavailable');
          return response.json();
        }).then(body => parseTimings(body,iso)).catch(error => {cache.delete(url);throw error;}).finally(() => clearTimeout(timeout));
        cache.set(url, request);
      }
      return cache.get(url);
    };
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = {timeMinutes,getParisClock,getNextPrayer,validSchedule,getDailyUrl,parseTimings,createClient};
  if (typeof document === 'undefined') return;
  const getDailySchedule = createClient((...args) => fetch(...args));
  const cleanups = new WeakMap();
  const init = root => {
    if (root.dataset.ready) return;
    const select = root.querySelector('[data-calendar-select]');
    const controls = root.querySelector('[data-calendar-controls]');
    const panels = [...root.querySelectorAll('[data-calendar-panel]')];
    if (!select || !panels.length) return;
    root.dataset.ready = 'true';
    root.classList.add('cto-calendar--enhanced');
    const today = root.querySelector('[data-calendar-today]');
    const labels = today?.dataset;
    const dateNode = root.querySelector('[data-today-date]');
    const cityNode = root.querySelector('[data-today-city]');
    const statusNode = root.querySelector('[data-today-status]');
    const retry = root.querySelector('[data-today-retry]');
    const times = [...root.querySelectorAll('[data-today-time]')];
    let coordinates = {};
    try {coordinates = JSON.parse(root.querySelector('[data-calendar-coordinates]')?.textContent || '{}');} catch {}
    let active = true, requestId = 0, activeKey = '', schedule, activeDay = '';
    const renderStatus = () => {
      if (!schedule) return;
      const next = getNextPrayer(schedule,getParisClock());
      const value = next ? labels.labelNext.replace('{prayer}',next.prayer).replace('{hours}',next.hours).replace('{minutes}',next.minutes) : labels.labelReady;
      if (statusNode.textContent !== value) statusNode.textContent = value;
    };
    const highlight = iso => {
      root.querySelectorAll('[data-calendar-date]').forEach(row => {
        if (row.dataset.calendarDate === iso) row.setAttribute('aria-current','date');
        else row.removeAttribute('aria-current');
      });
    };
    const loadToday = async (panel, iso) => {
      if (!today) return;
      const token = ++requestId;
      schedule = null;
      activeKey = `${panel.dataset.calendarPanel}:${iso}`;
      const city = select.selectedOptions[0].textContent.trim();
      cityNode.textContent = labels.labelCity.replace('{city}',city);
      dateNode.dateTime = iso;
      dateNode.textContent = new Intl.DateTimeFormat('fr-FR',{timeZone:'Europe/Paris',weekday:'long',day:'numeric',month:'long',year:'numeric'}).format(new Date(`${iso}T12:00:00Z`));
      today.hidden = false;
      today.setAttribute('aria-busy','true');
      retry.hidden = true;
      statusNode.setAttribute('aria-live','polite');
      statusNode.textContent = labels.labelLoading;
      times.forEach(node => {node.textContent='—';});
      try {
        const row = panel.querySelector(`[data-calendar-date="${iso}"]`);
        const daily = row && validSchedule(row.dataset) ? {imsak:row.dataset.imsak,fajr:row.dataset.fajr,maghrib:row.dataset.maghrib} : await getDailySchedule(iso,coordinates[panel.dataset.calendarCity]);
        if (!active || token !== requestId) return;
        schedule = daily;
        times.forEach(node => {node.textContent = daily[node.dataset.todayTime];});
        today.setAttribute('aria-busy','false');
        renderStatus();
        statusNode.setAttribute('aria-live','off');
      } catch {
        if (!active || token !== requestId) return;
        today.setAttribute('aria-busy','false');
        statusNode.textContent = labels.labelError;
        retry.hidden = false;
      }
    };
    const render = () => {
      let panel;
      panels.forEach(item => {
        item.open = true;
        item.hidden = item.dataset.calendarPanel !== select.value;
        if (!item.hidden) panel = item;
      });
      const {iso} = getParisClock();
      if (iso !== activeDay) {highlight(iso);activeDay=iso;}
      if (panel && activeKey !== `${panel.dataset.calendarPanel}:${iso}`) loadToday(panel,iso);
      else renderStatus();
    };
    controls.hidden = false;
    select.addEventListener('change',render);
    root.querySelector('[data-calendar-print]')?.addEventListener('click',() => window.print());
    retry?.addEventListener('click',() => {activeKey='';render();});
    render();
    const timer = setInterval(render,60000);
    cleanups.set(root,() => {active=false;requestId++;clearInterval(timer);select.removeEventListener('change',render);delete root.dataset.ready;});
  };
  const rootsIn = scope => [...(scope.matches?.('[data-cto-calendar]') ? [scope] : []),...scope.querySelectorAll('[data-cto-calendar]')];
  rootsIn(document).forEach(init);
  document.addEventListener('shopify:section:load',event => rootsIn(event.target).forEach(init));
  document.addEventListener('shopify:section:unload',event => rootsIn(event.target).forEach(root => cleanups.get(root)?.()));
})();
